/* prettier-ignore-file */
/**
 * Wizard step 2 — product selection.
 *
 * Quantities are clamped to the availability computed for the selected date
 * range. Draft shapes match useNewBookingStore so a booking started here stays
 * readable by NewBooking.js:
 *   selectedBikes  -> { [productName]: quantity }
 *   selectedAddOns -> { [productId]: true | { quantity } }   (true == per-bike)
 */

import React from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  TextInput,
} from "react-native";
import { formatCentsAsUSD } from "../../utils/formatting";

const StepProducts = ({
  products1,
  products2,
  product1Title,
  product2Title,
  selectedBikes,
  selectedAddOns,
  onBikeChange,
  onAddOnChange,
  availability,
  totalBikes,
  isAuthenticated,
  dailyRateOverrideCents,
  onRateOverrideChange,
}) => {
  const hasOverride =
    Number.isFinite(dailyRateOverrideCents) && dailyRateOverrideCents >= 0;
  const overrideText = hasOverride ? (dailyRateOverrideCents / 100).toFixed(2) : "";

  const handleOverrideText = (text) => {
    // Digits shift in from the right (POS-style): typing "500" reads as
    // "5.00" rather than accepting a typed decimal point.
    const digits = text.replace(/\D/g, "");
    if (!digits) {
      onRateOverrideChange(null);
      return;
    }
    onRateOverrideChange(parseInt(digits, 10));
  };
  const renderBike = (product) => {
    const qty = selectedBikes[product.name] || 0;
    const info = availability?.product1?.[product.id];
    const hasInventory = info?.hasInventory ?? Number(product.quantity) > 0;
    const available = hasInventory ? info?.available ?? 0 : null;
    const atMax = hasInventory && qty >= available;

    return (
      <View key={product.id} style={styles.productCard}>
        {product.imageUrl ? (
          // "contain", not "cover": product photos are wider than the card, so cover
          // crops the item itself — the snip showed a bike with its handlebars and
          // wheels cut off. Letterboxing against the muted background shows the whole
          // product, which is the point of the image here.
          <View style={styles.productImageFrame}>
            <Image
              source={{ uri: product.imageUrl }}
              style={styles.productImage}
              resizeMode="contain"
            />
          </View>
        ) : null}

        <View style={styles.productBody}>
          <Text style={styles.productName}>{product.name}</Text>
          {product.includedItems ? (
            <Text style={styles.productIncluded}>Includes: {product.includedItems}</Text>
          ) : null}

          <View style={styles.productFooter}>
            <View>
              <Text style={styles.productPrice}>{formatCentsAsUSD(product.price || 0)} / day</Text>
              <Text
                style={[
                  styles.productAvailability,
                  hasInventory && available === 0 && styles.availabilityNone,
                ]}
              >
                {!hasInventory
                  ? "Always available"
                  : available === 0
                  ? "None available for these dates"
                  : `${available} available`}
              </Text>
            </View>

            <View style={styles.qtyControls}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.qtyButton, qty === 0 && styles.qtyButtonDisabled]}
                onPress={() => onBikeChange(product, qty - 1)}
                disabled={qty === 0}
              >
                <Text style={styles.qtyButtonText}>−</Text>
              </TouchableOpacity>
              <Text style={styles.qtyValue}>{qty}</Text>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.qtyButton, atMax && styles.qtyButtonDisabled]}
                onPress={() => onBikeChange(product, qty + 1)}
                disabled={atMax}
              >
                <Text style={styles.qtyButtonText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderAddOn = (product) => {
    const option = product.option || "per-booking";
    const value = selectedAddOns[product.id];
    const info = availability?.product2?.[product.id];
    const hasInventory = info?.hasInventory ?? false;
    const available = hasInventory ? info?.available ?? 0 : null;

    const priceSuffix =
      option === "per-bike" ? " / bike" : option === "user-selection" ? " / day" : "";

    // per-bike add-ons are a yes/no choice applied to every bike in the order.
    if (option === "per-bike") {
      const checked = value === true;
      const blocked = totalBikes === 0;
      return (
        <TouchableOpacity
          key={product.id}
          style={[styles.addOnRow, blocked && styles.addOnRowDisabled]}
          onPress={() => !blocked && onAddOnChange(product, checked ? 0 : 1)}
          disabled={blocked}
        >
          <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
            {checked && <Text style={styles.checkboxMark}>✓</Text>}
          </View>
          <View style={styles.addOnInfo}>
            <Text style={styles.addOnName}>{product.name}</Text>
            <Text style={styles.addOnPrice}>
              {formatCentsAsUSD(product.price || 0)}
              {priceSuffix}
              {blocked ? ` · pick a ${product1Title.toLowerCase().replace(/s$/, "")} first` : ""}
            </Text>
          </View>
        </TouchableOpacity>
      );
    }

    const qty = value?.quantity || 0;
    const atMax = hasInventory && qty >= available;

    return (
      <View key={product.id} style={styles.addOnRow}>
        <View style={styles.addOnInfo}>
          <Text style={styles.addOnName}>{product.name}</Text>
          <Text style={styles.addOnPrice}>
            {formatCentsAsUSD(product.price || 0)}
            {priceSuffix}
            {hasInventory ? ` · ${available} available` : ""}
          </Text>
        </View>
        <View style={styles.qtyControls}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.qtyButton, qty === 0 && styles.qtyButtonDisabled]}
            onPress={() => onAddOnChange(product, qty - 1)}
            disabled={qty === 0}
          >
            <Text style={styles.qtyButtonText}>−</Text>
          </TouchableOpacity>
          <Text style={styles.qtyValue}>{qty}</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.qtyButton, atMax && styles.qtyButtonDisabled]}
            onPress={() => onAddOnChange(product, qty + 1)}
            disabled={atMax}
          >
            <Text style={styles.qtyButtonText}>+</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <ScrollView style={styles.container}>
      {/* Staff only — a negotiated rate is not something a customer may set. */}
      {isAuthenticated && (
        <View style={[styles.section, styles.overrideSection]}>
          <Text style={styles.overrideTitle}>Daily rate override</Text>
          <Text style={styles.overrideHint}>
            Optional. Charges every {product1Title.toLowerCase()} at this rate per day
            instead of its listed price. Leave blank to use normal pricing.
          </Text>
          <View style={styles.overrideRow}>
            <Text style={styles.overridePrefix}>$</Text>
            <TextInput
              style={styles.overrideInput}
              value={overrideText}
              onChangeText={handleOverrideText}
              placeholder="Listed price"
              placeholderTextColor="#AEB8B6"
              keyboardType="number-pad"
              inputMode="numeric"
            />
            <Text style={styles.overrideSuffix}>/ day each</Text>
          </View>
          {hasOverride && (
            <Text style={styles.overrideActive}>
              Override active — duration discounts do not apply on top of a negotiated rate.
            </Text>
          )}
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{product1Title}</Text>
        {(products1 || []).length === 0 ? (
          <Text style={styles.emptyText}>Nothing configured.</Text>
        ) : (
          products1.map(renderBike)
        )}
      </View>

      {(products2 || []).length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{product2Title}</Text>
          {products2.map(renderAddOn)}
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  section: { backgroundColor: "#FFFFFF", borderRadius: 8, padding: 16, marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2E3A38", marginBottom: 12 },

  overrideSection: { borderLeftWidth: 4, borderLeftColor: "#B4761B" },
  overrideTitle: { fontSize: 15, fontWeight: "700", color: "#7A4E12", marginBottom: 4 },
  overrideHint: { fontSize: 12, color: "#5C6B68", lineHeight: 17, marginBottom: 10 },
  overrideRow: { flexDirection: "row", alignItems: "center" },
  overridePrefix: { fontSize: 18, fontWeight: "700", color: "#5C6B68", marginRight: 6 },
  overrideInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontSize: 16, // at/above the iOS focus-zoom threshold
    fontWeight: "600",
    color: "#0F1A19",
    backgroundColor: "#FFFFFF",
  },
  overrideSuffix: { fontSize: 13, color: "#5C6B68", marginLeft: 8 },
  overrideActive: { fontSize: 12, color: "#7A4E12", marginTop: 8, fontWeight: "600" },
  emptyText: { fontSize: 13, color: "#8A9794" },

  productCard: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    marginBottom: 12,
    overflow: "hidden",
  },
  productImageFrame: {
    width: "100%",
    height: 150,
    backgroundColor: "#EDF1F0",
    alignItems: "center",
    justifyContent: "center",
    padding: 8, // keeps the product off the card edges when it letterboxes
  },
  productImage: { width: "100%", height: "100%" },
  productBody: { padding: 12 },
  productName: { fontSize: 15, fontWeight: "600", color: "#2E3A38" },
  productIncluded: { fontSize: 12, color: "#5C6B68", marginTop: 3 },
  productFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
  },
  productPrice: { fontSize: 14, color: "#2E3A38", fontWeight: "500" },
  productAvailability: { fontSize: 12, color: "#1F8A5B", marginTop: 2 },
  availabilityNone: { color: "#C4453D" },

  addOnRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    minHeight: 52, // comfortable phone tap target for the whole row
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  addOnRowDisabled: { opacity: 0.5 },
  addOnInfo: { flex: 1, paddingRight: 12 },
  addOnName: { fontSize: 14, color: "#2E3A38", fontWeight: "500" },
  addOnPrice: { fontSize: 12, color: "#5C6B68", marginTop: 2 },

  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: { backgroundColor: "#0E7C6B", borderColor: "#0E7C6B" },
  checkboxMark: { color: "#FFFFFF", fontSize: 14, fontWeight: "bold" },

  qtyControls: { flexDirection: "row", alignItems: "center" },
  qtyButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  qtyButtonDisabled: { backgroundColor: "#DDE3E2" },
  qtyButtonText: { color: "#FFFFFF", fontSize: 18, fontWeight: "bold", lineHeight: 20 },
  qtyValue: { minWidth: 40, textAlign: "center", fontSize: 15, fontWeight: "600", color: "#2E3A38" },
});

export default StepProducts;
